/*
  SherpaHolidays motion for the new sections (September 2026).
  Loaded with defer only by the sections that use it:
    [data-sh-alt]  trip altitude profile (sections/sh-altitude.liquid)
    [data-sh-map]  Nepal route map (sections/sh-route-map.liquid)

  The markup is complete without this file: full line with the climber
  on the first stop, every route drawn. Nothing here follows the scroll.
  Text is never hidden. Only transforms and stroke offsets change.
*/
(function () {
  'use strict';
  if (window.__shMotion) return;
  window.__shMotion = true;

  var reduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function reduced() {
    return !!(reduce && reduce.matches) || !('requestAnimationFrame' in window);
  }

  /* Altitude profile: the climber stands on a stop until a visitor
     points at another stop's column, taps it or focuses it. It then
     walks along the line to that stop in one motion that eases in and
     out, about 300 ms per stop passed (at least 400 ms, at most 2.4 s).
     A new target mid-walk takes over from the current position and
     speed, so the climber never jumps: ahead, it keeps its pace; behind,
     it slows to a stop, turns and walks back. Leaving the chart leaves it
     where it is. With reduced motion it moves at once.

     The position s is a stop index with a fraction (1.5 is halfway
     between the second and third stop). Each walk is a cubic curve from
     the current s and speed to the target, arriving at rest; a walk that
     keeps going the same way keeps its speed but is shortened when
     needed so it never passes the target. Each frame sets one transform
     from cached sizes, with no layout reads. */
  function initAltitude(root) {
    var plot = root.querySelector('[data-sh-alt-plot]');
    var walker = root.querySelector('[data-sh-alt-walker]');
    var hits = root.querySelectorAll('[data-sh-alt-hit]');
    if (!plot || !walker || !hits.length) return;
    var xs = root.getAttribute('data-xs').split(',').map(Number);
    var ys = root.getAttribute('data-ys').split(',').map(Number);
    var n = xs.length;
    if (n < 2 || ys.length !== n || hits.length !== n) return;
    /* Signature journeys: how each leg is traveled (w, f, h, r), so the
       climber hands over to a plane, helicopter or car on those legs */
    var modes = root.hasAttribute('data-modes') ? root.getAttribute('data-modes').split(',') : null;
    if (modes && modes.length !== n - 1) modes = null;

    var labels = [];
    var dots = [];
    var el = root.querySelectorAll('[data-sh-alt-label]');
    for (var a = 0; a < el.length; a++) labels[+el[a].getAttribute('data-sh-alt-label')] = el[a];
    el = root.querySelectorAll('[data-sh-alt-dot]');
    for (var b = 0; b < el.length; b++) dots[+el[b].getAttribute('data-sh-alt-dot')] = el[b];
    var readout = root.querySelector('[data-sh-alt-readout]');
    var readName = root.querySelector('[data-sh-alt-readout-name]');
    var readHeight = root.querySelector('[data-sh-alt-readout-height]');

    var PER_STOP = 300;
    var MIN_MS = 400;
    var MAX_MS = 2400;
    var STEP_PX = 14;
    var STEP_MS = 90;
    var STOP_MS = 180;

    var width = plot.clientWidth;
    var height = plot.clientHeight;
    var s = 0; /* where the climber is now */
    var target = 0; /* the stop it is walking to or standing on */
    var marked = -1; /* the highlighted stop */
    var walk = null; /* { t0, d, p0, p1, m0 } while walking */
    var then = -1; /* the stop to walk to after slowing down */
    var frame = 0;
    var back = false;
    var step = false;
    var travel = 0;
    var stepAt = 0;
    var lastX = 0;
    var lastY = 0;
    var pointerX = -1;
    var pointerY = -1;

    function place(pos) {
      var i = Math.min(Math.floor(pos), n - 2);
      var f = pos - i;
      return {
        x: (xs[i] + (xs[i + 1] - xs[i]) * f) * width,
        y: (ys[i] + (ys[i + 1] - ys[i]) * f) * height
      };
    }

    function paint() {
      var p = place(s);
      walker.style.transform = 'translate3d(' + p.x.toFixed(2) + 'px,' + (-p.y).toFixed(2) + 'px,0)';
      if (modes) {
        /* the leg it is on, or the one leaving the stop it stands on */
        var mode = modes[Math.min(Math.floor(s + 0.000001), n - 2)];
        if (walker.getAttribute('data-mode') !== mode) walker.setAttribute('data-mode', mode);
      }
      return p;
    }

    /* position and speed (stops per ms) of the current walk at time t */
    function sample(t) {
      var u = (t - walk.t0) / walk.d;
      if (u >= 1) return { s: walk.p1, v: 0, done: true };
      if (u < 0) u = 0;
      var u2 = u * u;
      var u3 = u2 * u;
      var pos = (2 * u3 - 3 * u2 + 1) * walk.p0 + (u3 - 2 * u2 + u) * walk.m0 + (-2 * u3 + 3 * u2) * walk.p1;
      var dv = (6 * u2 - 6 * u) * walk.p0 + (3 * u2 - 4 * u + 1) * walk.m0 + (-6 * u2 + 6 * u) * walk.p1;
      return { s: pos, v: dv / walk.d, done: false };
    }

    function setStep(on) {
      if (on === step) return;
      step = on;
      walker.classList.toggle('is-step', on);
    }

    function tick(now) {
      frame = 0;
      if (!walk) return;
      var at = sample(now);
      s = at.s;
      var p = paint();
      /* swap the two walking poses every few px of travel, never faster
         than a natural stride */
      travel += Math.abs(p.x - lastX) + Math.abs(p.y - lastY);
      lastX = p.x;
      lastY = p.y;
      if (travel >= STEP_PX && now - stepAt >= STEP_MS) {
        travel = 0;
        stepAt = now;
        setStep(!step);
      }
      if (at.done) {
        walk = null;
        if (then < 0) {
          setStep(false);
          return;
        }
        /* stopped after a change of direction: turn and walk back */
        var next = then;
        then = -1;
        plan(next, now, 0);
        return;
      }
      frame = requestAnimationFrame(tick);
    }

    function mark(i) {
      if (i === marked) return;
      if (labels[marked]) labels[marked].classList.remove('is-target');
      marked = i;
      if (labels[i]) labels[i].classList.add('is-target');
      read();
    }

    /* the target stop is named above the chart when it has no visible
       label (labels are dropped on narrow screens to keep them apart) */
    function read() {
      if (!readout || marked < 0) return;
      var shown = labels[marked] && labels[marked].offsetWidth > 0;
      if (!shown) {
        readName.textContent = hits[marked].getAttribute('data-name');
        readHeight.textContent = hits[marked].getAttribute('data-height');
      }
      readout.classList.toggle('is-on', !shown);
    }

    function face(delta) {
      if (delta === 0) return;
      back = delta < 0;
      walker.classList.toggle('is-back', back);
    }

    /* a walk from s, moving at v0 at time now, to stop i */
    function plan(i, now, v0) {
      var delta = i - s;
      face(delta);
      var d = Math.min(MAX_MS, Math.max(MIN_MS, PER_STOP * Math.abs(delta)));
      var m0 = 0;
      if (v0 * delta > 0) {
        /* same direction: keep the speed; a cubic that starts at speed
           m0 stays short of the target while m0 is at most 3 times the
           distance, so shorten the walk if needed */
        d = Math.min(d, (3 * delta) / v0);
        m0 = v0 * d;
      }
      walk = { t0: now, d: d, p0: s, p1: i, m0: m0 };
      if (!frame) frame = requestAnimationFrame(tick);
    }

    function go(i) {
      if (i === target && (walk || s === i)) {
        mark(i);
        return;
      }
      var now = performance.now();
      var v0 = 0;
      if (walk) {
        var at = sample(now);
        s = at.s;
        v0 = at.done ? 0 : at.v;
      }
      target = i;
      then = -1;
      mark(i);
      var delta = i - s;
      if (reduced() || Math.abs(delta) < 0.001) {
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
        walk = null;
        s = i;
        face(delta);
        setStep(false);
        paint();
        return;
      }
      var p = place(s);
      lastX = p.x;
      lastY = p.y;
      if (v0 * delta < 0) {
        /* the target is behind: ease to a stop first (a cubic that
           starts at speed v0 and covers half of v0 times its length
           ends at rest without turning back), then walk to the target */
        var t = STOP_MS;
        var run = (v0 * t) / 2;
        var edge = v0 > 0 ? n - 1 - s : -s;
        if (Math.abs(run) > Math.abs(edge)) {
          run = edge;
          t = (2 * run) / v0;
        }
        if (Math.abs(run) > 0.001) {
          walk = { t0: now, d: t, p0: s, p1: s + run, m0: v0 * t };
          then = i;
          if (!frame) frame = requestAnimationFrame(tick);
          return;
        }
        v0 = 0;
      }
      plan(i, now, v0);
    }

    function indexOf(btn) {
      return +btn.getAttribute('data-sh-alt-hit');
    }

    function focusStop(i) {
      if (i < 0 || i >= n) return;
      hits[i].focus();
    }

    /* Only a pointer that moves counts. When the page scrolls under a
       resting pointer the browser sends pointer events at the same
       screen position; those are ignored, so scrolling never moves the
       climber. */
    function onPoint(e) {
      if (e.pointerType === 'touch') return;
      if (e.clientX === pointerX && e.clientY === pointerY) return;
      go(indexOf(e.currentTarget));
    }

    function onClick(e) {
      go(indexOf(e.currentTarget));
    }

    function onFocus(e) {
      var btn = e.currentTarget;
      var i = indexOf(btn);
      for (var k = 0; k < n; k++) hits[k].tabIndex = k === i ? 0 : -1;
      var visible = true;
      try {
        visible = btn.matches(':focus-visible');
      } catch (err) {}
      if (dots[i]) dots[i].classList.toggle('is-focus', visible);
      go(i);
    }

    function onBlur(e) {
      var i = indexOf(e.currentTarget);
      if (dots[i]) dots[i].classList.remove('is-focus');
    }

    function onKey(e) {
      var i = indexOf(e.currentTarget);
      var next = -1;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = i + 1;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = i - 1;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = n - 1;
      else return;
      e.preventDefault();
      focusStop(Math.max(0, Math.min(n - 1, next)));
    }

    for (var h = 0; h < n; h++) {
      hits[h].addEventListener('pointermove', onPoint);
      hits[h].addEventListener('click', onClick);
      hits[h].addEventListener('focus', onFocus);
      hits[h].addEventListener('blur', onBlur);
      hits[h].addEventListener('keydown', onKey);
    }

    /* runs after the column's own handler, so onPoint compares with the
       previous position */
    window.addEventListener(
      'pointermove',
      function (e) {
        pointerX = e.clientX;
        pointerY = e.clientY;
      },
      { passive: true }
    );

    function measure() {
      width = plot.clientWidth;
      height = plot.clientHeight;
      if (!walk) paint();
      read();
    }

    if ('ResizeObserver' in window) {
      new ResizeObserver(measure).observe(plot);
    } else {
      window.addEventListener('resize', measure);
    }
    root.classList.add('is-live');
    paint();
  }

  /* Route map: each route draws once when the map comes into view, one
     after another. The trigger is the map itself, not the whole section
     with its trip list, so the drawing starts when the map is on screen.
     Skipped with reduced motion, in the theme editor, or without
     IntersectionObserver; the routes are then simply drawn. */
  function initMap(root) {
    var routes = root.querySelectorAll('[data-sh-map-route]');
    if (!routes.length) return;
    if (reduced() || (window.Shopify && window.Shopify.designMode) || !('IntersectionObserver' in window)) return;
    var figure = root.querySelector('.sh-map__figure') || root;
    root.classList.add('is-live');
    var io = new IntersectionObserver(
      function (entries) {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        root.classList.add('is-drawn');
      },
      { threshold: 0.4 }
    );
    io.observe(figure);
  }

  function start() {
    var alts = document.querySelectorAll('[data-sh-alt]');
    for (var i = 0; i < alts.length; i++) initAltitude(alts[i]);
    var maps = document.querySelectorAll('[data-sh-map]');
    for (var j = 0; j < maps.length; j++) initMap(maps[j]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
